const isFieldRequired = (requiredFields: string[], fieldName: string) => {
  return (requiredFields || []).includes(fieldName);
};

export const getUnifiedDataFromProjectValues = ({
  values,
  query: gqlQuery,
  schema = { properties: {}, required: [], type: 'object' },
}: {
  values: any;
  schema?: JSONSchema;
  query?: string;
}) => {
  const initialValues: { [key: string]: string | number | null | string[] } = { ...values };

  const createFormFields = (schema: any): DynamicFormField[] =>
    Object.keys(schema.properties ?? {})
      //@ts-ignore
      .map<DynamicFormField>((keyName) => {
        const schemaFieldData = schema.properties[keyName];

        const inputType: DynamicFormField['inputType'] = schemaFieldData?.inputType || 'text';
        // @todo support if an input may handle different types
        // const type: DynamicFormField['type'] = schemaFieldData?.type || 'string';

        const fieldData: BaseDynamicFormField = {
          name: keyName,
          order: schemaFieldData.inputOrder || schemaFieldData?.inputOptions?.order || 0,
          required: isFieldRequired(schema?.required || [], keyName),
          label: schemaFieldData?.displayName || schemaFieldData?.title || keyName,
          helperText: schemaFieldData?.description || undefined,
        };

        switch (inputType) {
          case 'text':
          case 'textarea':
            return {
              ...fieldData,
              type: 'string',
              inputType,
              inputOptions: {
                defaultValue: schemaFieldData?.inputOptions?.defaultValue,
              },
            };

          case 'number': {
            return {
              ...fieldData,
              type: 'number',
              inputType,
            };
          }

          //@ts-ignore
          case 'pages-picker':
            return {
              ...fieldData,
              type: 'array', // Make sure type is array
              inputType,
              inputOptions: schemaFieldData?.inputOptions || {},
            };

          case 'video-picker':
          case 'image-picker':
            return {
              ...fieldData,
              type: 'number',
              inputType,
              inputOptions: {
                sourcePath: schemaFieldData.inputOptions?.sourcePath || schemaFieldData.sourcePath,
                pickerVariant: schemaFieldData.inputOptions?.pickerVariant || 'default',
              },
            };

          case 'checkbox':
            return {
              ...fieldData,
              type: 'boolean',
              inputType,
              inputOptions: {},
            };

          case 'checkbox-group':
            return {
              ...fieldData,
              type: 'string',
              inputType,
              inputOptions: {
                checkboxOptions: schemaFieldData?.inputOptions?.checkboxOptions || [],
              },
            };

          case 'select':
            return {
              ...fieldData,
              type: 'string',
              inputType,
              inputOptions: {
                selectOptions: schemaFieldData?.inputOptions?.selectOptions || [],
              },
            };

          case 'slides-picker':
            return {
              ...fieldData,
              type: 'object',
              inputType,
              inputOptions: {
                sourcePath: schemaFieldData?.inputOptions?.sourcePath || '',
              },
            };

          case 'assets-picker':
            return {
              ...fieldData,
              type: 'object',
              inputType,
              inputOptions: {
                sourcePath: schemaFieldData?.inputOptions?.sourcePath || '',
                unique: schemaFieldData?.inputOptions?.unique,
              },
            };

          case 'ordered-list':
            return {
              ...fieldData,
              type: 'array',
              items: createFormFields(schemaFieldData.items ?? {}),
              inputType,
              minItems: schemaFieldData.minItems,
              maxItems: schemaFieldData.maxItems,
              inputOptions: {
                gqlQuery: schemaFieldData?.inputOptions?.gqlQuery || gqlQuery,
                gqlQueryKey: schemaFieldData?.inputOptions?.gqlQueryKey,
                previewComponent: schemaFieldData?.inputOptions?.previewComponent,
                variant: schemaFieldData?.inputOptions?.variant ?? 'default',
                filterType: schemaFieldData?.inputOptions?.filterType || undefined,
              },
            };

          case 'queryKey':
            return {
              ...fieldData,
              type: 'array',
              items: createFormFields(schemaFieldData.items ?? {}),
              inputType,
              inputOptions: {
                gqlQueryKey: schemaFieldData?.inputOptions?.gqlQueryKey,
              },
            };

          case 'object':
            return {
              ...fieldData,
              type: 'object',
              items: createFormFields(schemaFieldData),
              inputType,
            };

          default:
            return {
              ...fieldData,
              type: 'string',
              inputType,
            };
        }
      })
      .sort((a, b) => a.order - b.order);

  // Step 2) map schema to form fields
  const allFormFields = createFormFields(schema).sort((a, b) => a.order - b.order);

  // exclude queryKey fields from the form UI — they are auto-populated from GQL query results
  // also remove object fields that end up with no visible children
  const filterListingFields = (fields: DynamicFormField[]): DynamicFormField[] =>
    fields
      .filter((field) => field.inputType !== 'queryKey')
      .map((field) =>
        'items' in field && Array.isArray(field.items) ? { ...field, items: filterListingFields(field.items) } : field,
      )
      .filter((field) => !('items' in field && Array.isArray(field.items) && field.items.length === 0));

  const formFields = filterListingFields(allFormFields);

  const steps: DynamicFormData['steps'] = [];
  for (const field of formFields) {
    if (field.inputType === 'object') {
      steps.push({
        name: field.label,
        inputs: [field],
      });
    }
  }

  if (steps.length === 0) {
    steps.push({
      name: (schema as any).title || 'Provide details',
      inputs: formFields,
    });
  }

  // Step 3) set initial values if not provided from api in Step 1
  const createInitialValues = (formFields: DynamicFormField[], initialValues: Record<string, any>) => {
    for (const formField of formFields) {
      if (initialValues[formField.name] !== undefined && initialValues[formField.name] !== null) continue;

      switch (formField.inputType) {
        case 'checkbox':
          initialValues[formField.name] = false;
          break;

        case 'checkbox-group':
          initialValues[formField.name] = [];
          break;

        case 'ordered-list':
          initialValues[formField.name] = [];
          break;

        case 'pages-picker':
          initialValues[formField.name] = [];
          break;

        case 'slides-picker':
          initialValues[formField.name] = {};
          break;

        case 'assets-picker':
          initialValues[formField.name] = {};
          break;

        case 'object':
          initialValues[formField.name] = {};
          createInitialValues(formField.items, initialValues[formField.name]);
          break;

        case 'select':
          initialValues[formField.name] = null;
          break;

        case 'video-picker':
        case 'image-picker':
          initialValues[formField.name] = null;
          break;

        case 'text':
        case 'textarea':
          initialValues[formField.name] = formField?.inputOptions?.defaultValue || '';
          break;

        case 'number':
          initialValues[formField.name] = 0;
          break;

        default:
          initialValues[formField.name] = '';
          break;
      }
    }
  };

  createInitialValues(formFields, initialValues);

  // Modify schema to allow null values for image-picker and video-picker fields
  const modifySchemaForImagePickers = (schemaObj: any, fields: DynamicFormField[]): any => {
    if (!schemaObj.properties) return schemaObj;

    const modifiedProperties = { ...schemaObj.properties };

    fields.forEach((field) => {
      const fieldName = field.name;

      if (modifiedProperties[fieldName]) {
        if (field.inputType === 'image-picker' || field.inputType === 'video-picker') {
          const originalField = modifiedProperties[fieldName];
          const { type, ...restOfField } = originalField as any;
          modifiedProperties[fieldName] = {
            ...restOfField,
            // @ts-ignore - Using anyOf to allow multiple types
            anyOf: [{ type: 'number' }, { type: 'null' }, { type: 'string', maxLength: 0 }],
          };
        } else if (field.inputType === 'object' && field.items) {
          modifiedProperties[fieldName] = modifySchemaForImagePickers(modifiedProperties[fieldName], field.items);
        }
      }
    });

    return {
      ...schemaObj,
      properties: modifiedProperties,
    };
  };

  const modifiedSchema = modifySchemaForImagePickers(schema, formFields);

  return { schema: modifiedSchema, initialValues, formFields, steps: steps };
};
